import olLayerTile from 'ol/layer/Tile.js';
import olLayerVector from 'ol/layer/Vector.js';
import {toDegrees} from 'ol/math.js';
import {toContext} from 'ol/render.js';
import {getWidth as getExtentWidth, getHeight as getExtentHeight} from 'ol/extent.js';
import {MVTEncoder} from '@geoblocks/print';
import type BaseCustomizer from '@geoblocks/mapfishprint/src/BaseCustomizer';
import type Map from 'ol/Map';
import {MapFishPrintLayer, MapFishPrintMap} from '@geoblocks/mapfishprint/src/mapfishprintTypes';
import BaseLayer from 'ol/layer/Base';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector';
import {Geometry} from 'ol/geom';
import appPrintVectorEncoder from '@geoblocks/mapfishprint/src/VectorEncoder';
import MapfishPrintBaseEncoder, {asOpacity} from '@geoblocks/mapfishprint/src/MapfishPrintBaseEncoder';


export interface Options {
  drawInnerBuffer: (vectorContext, geometry) => void
  swissparksFillResolution: number
  printUrl: string
}

export default class MapfishPrintSCHMEncoder extends MapfishPrintBaseEncoder {
  drawInnerBuffer: (vectorContext, geometry) => void
  swissparksFillResolution: number
  scratchCanvas: HTMLCanvasElement

  constructor(options: Options) {
    super(options.printUrl);
    this.drawInnerBuffer = options.drawInnerBuffer;
    this.swissparksFillResolution = options.swissparksFillResolution;
    this.scratchCanvas = document.createElement('canvas');
  }


  async encodeMap(map: Map, scale: number, printResolution: number, dpi: number, customizer: BaseCustomizer): Promise<MapFishPrintMap> {
    const view = map.getView();
    const center = view.getCenter();
    const projection = view.getProjection().getCode();
    const rotation = toDegrees(view.getRotation());
    const layers = await this.mapToLayers(map, printResolution, customizer);

    const pdfA = scale !== 1400000;
    const spec = {
      center,
      // when printing at 254 dpi, there exists images which can be use at approximately the native resolution,
      // for all scales except 1'400'000.
      // It is a problem when combined with the print config "allowTransparency: false" (it looks ugly blurry).
      // The exact reason is unknown. Maybe a bad scaling somewhere using a nearest interpolation but it was not found.
      // As a consequence, at scale 1'400'000 the background an route layers are not merged together.
      dpi,
      pdfA,
      projection,
      rotation,
      scale,
      layers
    } as MapFishPrintMap;
    return spec;
  }

  async encodeLayer(layer: BaseLayer, printResolution: number, customizer: BaseCustomizer): Promise<MapFishPrintLayer[]| MapFishPrintLayer | null> {
    if (!layer.getVisible() || printResolution < layer.getMinResolution() || printResolution >= layer.getMaxResolution()) {
      return null;
    }
    // transform route MVT/swisspark layer to plain vector layer
    const kind = layer.get('kind');

    if (kind === 'mvt_route_layer') {
      const encoder = new MVTEncoder();
      const printExtent = customizer.printExtent;
      const width = getExtentWidth(printExtent) / printResolution;
      const height = getExtentHeight(printExtent) / printResolution;
      const canvasSize: [number, number] = [width, height];
      const printOptions = {
        layer: layer as VectorTileLayer,
        printExtent: customizer.printExtent,
        tileResolution: printResolution,
        styleResolution: printResolution,
        canvasSize: canvasSize,

      };
      const r = await encoder.encodeMVTLayer(printOptions);
      return r
        .filter(rr => rr.baseURL.length > 6)
        .map(rr => Object.assign({
          type: 'image',
          name: layer.get('name'),
          opacity: 1,
          imageFormat: 'image/png',
        }, rr) as MapFishPrintLayer
        );
    }
    if (kind === 'geojson_swisspark_layer') {
      const vl = layer as VectorLayer<VectorSource>;
      return this.encodeAsImageLayer(vl, printResolution, customizer);
    }
    if (layer instanceof olLayerTile) {
      return this.encodeTileLayer(layer, customizer);
    } else if (layer instanceof olLayerVector) {
      const encoded = new appPrintVectorEncoder(layer, customizer).encodeVectorLayer(printResolution)!;
      const renderAsSvg = layer.get('renderAsSvg');
      if (renderAsSvg !== undefined) {
        encoded.renderAsSvg = renderAsSvg;
      }
      return encoded;
    } else {
      return null;
    }
  }

  async encodeAsImageLayer(layer: VectorLayer<VectorSource>, resolution: number, customizer: BaseCustomizer) {
    const printExtent = customizer.printExtent;
    const width = getExtentWidth(printExtent) / resolution;
    const height = getExtentHeight(printExtent) / resolution;
    /** @type {[number, number]} */
    const size = [width, height];
    // FIXME: test the 2 sizes are the same (no, there is a slight difference. Is there a buffer somewhere?)
    console.log(size, [2689, 1697]);
    const vectorContext = toContext(this.scratchCanvas.getContext('2d')!, {
      size,
      pixelRatio: 1,
    });
    const coordinateToPixelTransform = this.createCoordinateToPixelTransform(printExtent, resolution, size);
    const features = layer.getSource()!.getFeatures();
    const styleFunction = layer.getStyleFunction();
    let additionalDraw = function(_: Geometry) {};
    if (layer.get('layername') === 'swisspark' && resolution <= this.swissparksFillResolution) {
      additionalDraw = (geometry: Geometry) => {
        this.drawInnerBuffer(vectorContext, geometry);
      };
    }

    this.drawFeaturesToContext(features, styleFunction, resolution, coordinateToPixelTransform, vectorContext, additionalDraw);

    return {
      type: 'image',
      extent: printExtent,
      imageFormat: 'image/png', // this is the target image format in the mapfish-print
      opacity: 1, // FIXME: mapfish-print is not handling the opacity correctly for images with dataurl.
      name: layer.get('name'),
      baseURL: asOpacity(this.scratchCanvas, layer.getOpacity()).toDataURL('PNG')
    };
  }
}
